import { motion } from "motion/react";
import { useState, useEffect } from "react";

export function HeroSalesSection() {
  const [scanProgress, setScanProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setScanProgress((prev) => (prev >= 100 ? 0 : prev + 1));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,245,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,245,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

      {/* Animated Scan Lines */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00F5FF]/5 to-transparent"
        animate={{ y: ["-100%", "100%"] }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Overline */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <span className="font-['Roboto_Mono'] text-[#00F5FF] text-sm tracking-[0.3em] uppercase">
              Sistema Nervoso Central para WooCommerce
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-['Sora'] text-5xl md:text-7xl font-bold mb-6 leading-tight"
          >
            <span className="text-white">WP Total Tracker:</span>
            <br />
            <span className="text-[#00F5FF] drop-shadow-[0_0_30px_rgba(0,245,255,0.5)]">
              Tudo Rastreado,
            </span>
            <br />
            <span className="text-[#00F5FF] drop-shadow-[0_0_30px_rgba(0,245,255,0.5)]">
              Tudo Conectado,
            </span>
            <br />
            <span className="text-[#00F5FF] drop-shadow-[0_0_30px_rgba(0,245,255,0.5)]">
              Tudo Sob Controle.
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="font-['Inter'] text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Pare de adivinhar. Pare de perder vendas para o "tráfego desconhecido". 
            Transforme seu WordPress em uma{" "}
            <span className="text-[#10B981] font-semibold">máquina de inteligência de receita</span>{" "}
            com precisão cirúrgica e PhD.
          </motion.p>

          {/* Key Questions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-[#0B0E14]/80 backdrop-blur-xl border border-[#00F5FF]/20 rounded-2xl p-8 mb-10 max-w-3xl mx-auto"
            style={{
              boxShadow: "0 0 40px rgba(0,245,255,0.1), inset 0 0 60px rgba(0,245,255,0.03)",
            }}
          >
            <p className="font-['Inter'] text-lg text-gray-200 mb-4">
              Você sabe <span className="text-[#FF4D00] font-semibold">quem</span> está no seu site agora?
            </p>
            <p className="font-['Inter'] text-lg text-gray-200 mb-6">
              Você sabe exatamente <span className="text-[#FF4D00] font-semibold">por que</span> o Lead X abandonou o carrinho na etapa 3?
            </p>
            <p className="font-['Roboto_Mono'] text-sm text-[#00F5FF] border-l-4 border-[#00F5FF] pl-4">
              Se a resposta for "não" ou "talvez", você está operando no escuro.
            </p>
          </motion.div>

          {/* Value Prop */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="font-['Inter'] text-xl text-gray-300 mb-12 max-w-3xl mx-auto"
          >
            O WP Total Tracker é a primeira ferramenta de{" "}
            <span className="text-[#00F5FF] font-semibold">Analytics Nível PhD</span> para WordPress.
            <br />
            Mais que números, entregamos{" "}
            <span className="text-[#10B981] font-bold text-2xl drop-shadow-[0_0_20px_rgba(16,185,129,0.5)]">
              Identidade
            </span>
            .
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
          >
            <button className="group relative px-12 py-5 bg-[#FF4D00] text-white font-['Sora'] text-lg font-bold rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(255,77,0,0.6)]">
              <span className="relative z-10">Quero Total Controle dos Meus Dados</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-[#FF4D00] via-[#FF6B00] to-[#FF4D00]"
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              />
            </button>
          </motion.div>

          {/* Demo Visual - Scan Progress */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-16 bg-[#0B0E14]/60 backdrop-blur-xl border border-[#00F5FF]/30 rounded-3xl p-8 max-w-4xl mx-auto"
            style={{
              boxShadow: "0 0 60px rgba(0,245,255,0.15), inset 0 0 80px rgba(0,245,255,0.05)",
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="font-['Roboto_Mono'] text-[#00F5FF] text-sm">
                DASHBOARD LOADING...
              </span>
              <span className="font-['Roboto_Mono'] text-[#10B981] text-sm">
                {scanProgress}%
              </span>
            </div>
            <div className="h-2 bg-[#0B0E14] rounded-full overflow-hidden border border-[#00F5FF]/20">
              <motion.div
                className="h-full bg-gradient-to-r from-[#00F5FF] to-[#10B981]"
                style={{ width: `${scanProgress}%` }}
                transition={{ duration: 0.05 }}
              />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-8">
              {["Chart.js Rendering", "Identity Resolution V32", "Cache System Active"].map((label, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.4 + i * 0.1 }}
                  className="bg-[#0B0E14]/80 border border-[#00F5FF]/20 rounded-lg p-4 text-center"
                >
                  <div className="w-3 h-3 bg-[#10B981] rounded-full mx-auto mb-2 animate-pulse" />
                  <span className="font-['Roboto_Mono'] text-xs text-gray-400">
                    {label}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Floating Particles */}
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-[#00F5FF] rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.8, 0.2],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </section>
  );
}
